import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Navbar, Nav, Modal, Form, Alert, InputGroup } from 'react-bootstrap';
import './LandingPage.css';
import { 
  Cloud, 
  Shield, 
  BarChart, 
  Envelope, 
  CheckCircle, 
  Circle,
  ArrowRight,
  Play,
  People,
  Globe,
  Lightning,
  Globe2,
  Inbox,
  Eye,
  EyeSlash
} from 'react-bootstrap-icons';
import { useAuth0 } from '@auth0/auth0-react';
import Logo from './Logo';

const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'lower', label: 'Lowercase letter (a\u2013z)', test: (p) => /[a-z]/.test(p) },
  { key: 'upper', label: 'Uppercase letter (A\u2013Z)', test: (p) => /[A-Z]/.test(p) },
  { key: 'number', label: 'Number (0\u20139)', test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'Special character (!@#$%^&*)', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PasswordChecklist({ password }) {
  const typesNeeded = 3;
  const typesPassing = PASSWORD_RULES.slice(1).filter((r) => r.test(password)).length;
  return (
    <ul className="list-unstyled mb-0 mt-1" style={{ fontSize: '0.82rem' }}>
      {PASSWORD_RULES.map((rule) => {
        const ok = rule.test(password);
        return (
          <li key={rule.key} className="d-flex align-items-center gap-1" style={{ color: ok ? '#198754' : '#6c757d' }}>
            {ok ? <CheckCircle size={12} /> : <Circle size={12} />}
            <span>{rule.label}</span>
          </li>
        );
      })}
      <li className="mt-1" style={{ color: typesPassing >= typesNeeded ? '#198754' : '#6c757d', fontSize: '0.78rem' }}>
        ({typesPassing}/{typesNeeded} character types required)
      </li>
    </ul>
  );
}

const LandingPage = () => {
  const { loginWithRedirect } = useAuth0();
  const [navSolid, setNavSolid] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Invite code modal state
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    workPhone: '',
    mobilePhone: '',
    address: '',
    reason: ''
  });

  // Basic Contact Us modal
  const [showContactModal, setShowContactModal] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);
  const [sentContact, setSentContact] = useState(false);
  const [contactBasic, setContactBasic] = useState({ name: '', email: '', message: '' });

  const handleSignIn = () => {
    // Get redirect URI dynamically - always use current origin at runtime
    // Remove any trailing slashes - Auth0 is strict about exact URL matches
    let redirectUri = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:3002';
    redirectUri = redirectUri.replace(/\/$/, ''); // Remove trailing slash if present
    
    console.log('🔍 handleSignIn - redirect_uri:', redirectUri);
    console.log('🔍 handleSignIn - window.location:', window.location.href);
    
    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        redirect_uri: redirectUri // Explicitly set to current origin (no trailing slash)
      }
    });
  };

  // Turn the navbar background on when the Features section is in view
  useEffect(() => {
    const section = document.getElementById('features');
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setNavSolid(entry.isIntersecting);
      },
      { root: null, threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const openCodeModal = (e) => {
    if (e) e.preventDefault();
    setInviteCode('');
    setInviteEmail('');
    setInvitePassword('');
    setInviteError('');
    setInviteSubmitting(false);
    setShowPassword(false);
    setShowCodeModal(true);
  };

  const getClientIdForHostname = () => {
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname;
      if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
        return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx';
      }
      if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
        return 'Ymq5KisXUcYdVTxnZKewnISyNO2JU81q';
      }
    }
    return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx';
  };

  const handleInviteSignup = async (e) => {
    e.preventDefault();
    const code = inviteCode.trim();
    const email = inviteEmail.trim();
    const password = invitePassword;
    if (!code) { setInviteError('Please enter your access code.'); return; }
    if (!email) { setInviteError('Please enter your email address.'); return; }
    if (!password || password.length < 8) { setInviteError('Password must be at least 8 characters.'); return; }

    setInviteSubmitting(true);
    setInviteError('');
    try {
      const domain = process.env.REACT_APP_AUTH0_DOMAIN || 'krossglobalmarketing.us.auth0.com';
      const clientId = getClientIdForHostname();

      // Call /dbconnections/signup directly so user_metadata reaches the Pre-User Registration Action.
      const res = await fetch(`https://${domain}/dbconnections/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          email,
          password,
          connection: 'Username-Password-Authentication',
          user_metadata: { signup_code: code },
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code_str = body.code || '';
        const descStr = typeof body.description === 'string' ? body.description : '';
        let msg;
        if (code_str === 'user_exists' || descStr.toLowerCase().includes('already exists')) {
          msg = 'An account with this email already exists. Click Cancel and use "Sign In" instead.';
        } else if (code_str === 'invalid_signup') {
          msg = descStr || 'Signup was rejected. Please check your access code and try again.';
        } else if (body.name === 'PasswordStrengthError' || code_str === 'invalid_password'
          || (typeof body.description === 'object' && body.description?.rules)) {
          msg = 'Password does not meet the requirements above. Make sure at least 3 of the 4 character types are satisfied.';
        } else {
          msg = descStr || body.message || body.policy || `Signup failed (${res.status})`;
        }
        setInviteError(String(msg));
        setInviteSubmitting(false);
        return;
      }

      // Account created — redirect to Auth0 login so the user can sign in immediately.
      setShowCodeModal(false);
      loginWithRedirect({
        authorizationParams: {
          prompt: 'login',
          login_hint: email,
          redirect_uri: (process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin).replace(/\/$/, ''),
        },
      });
    } catch (err) {
      setInviteError('Unable to create account. Please try again.');
      setInviteSubmitting(false);
    }
  };

  const openContact = (e) => {
    if (e) e.preventDefault();
    setShowContact(true);
  };
  const scrollToInvite = () => {
    const el = document.getElementById('invite-access');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeContact = () => {
    setShowContact(false);
    setSubmitted(false);
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...contact,
        submittedAt: new Date().toISOString(),
      };
      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        await fetch(`${apiUrl}/invite-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(() => {});
      }
      setSubmitted(true);
      setContact({
        firstName: '', lastName: '', email: '', company: '', workPhone: '', mobilePhone: '', address: '', reason: ''
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <Navbar
        expand="lg"
        className="fixed-top"
        style={{
          backgroundColor: navSolid ? 'rgba(10, 14, 39, 0.35)' : 'transparent',
          backdropFilter: navSolid ? 'saturate(160%) blur(4px)' : 'none',
          transition: 'background-color 200ms ease, backdrop-filter 200ms ease',
          zIndex: 10
        }}
      >
        <Container fluid>
          <Navbar.Brand href="#home" className="me-4">
            <Logo size="xxl" />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" style={{ borderColor: 'white' }} />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link href="#features" className="text-white">Features</Nav.Link>
              {/* Pricing link hidden - section is commented out below (invite-only mode) */}
              {/* <Nav.Link href="#pricing" className="text-white">Pricing</Nav.Link> */}
              <Nav.Link href="#about" className="text-white">About</Nav.Link>
              <Nav.Link 
                href="#"
                className="text-white"
                onClick={openCodeModal}
              >
                Have an access code?
              </Nav.Link>
              <Nav.Link 
                href="#"
                className="text-white"
                onClick={(e) => {
                  e.preventDefault();
                  handleSignIn();
                }}
              >
                Sign In
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section id="home" className="hero-section position-relative" style={{ minHeight: '100vh', marginTop: 0 }}>
        {/* Full-width hero image background */}
        <img
          src="https://krossglobalwebsiteimages.s3.us-east-1.amazonaws.com/kgm%20hero%20image%20v2.png"
          alt="KrossGlobal Marketing Hero"
          className="hero-background-image"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
          onError={(e) => {
            console.log('Hero image failed to load:', e.target.src);
            e.target.style.display = 'none';
            e.target.parentNode.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
          }}
        />
        
        {/* Gradient overlay for full width */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to right, transparent 0%, transparent 30%, rgba(0, 0, 0, 0.2) 50%, rgba(0, 0, 0, 0.4) 70%, rgba(0, 0, 0, 0.5) 100%)',
            zIndex: 1
          }}
        />
        
        {/* Content overlay */}
        <div 
          className="hero-content-overlay"
          style={{
            position: 'relative',
            zIndex: 2,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            paddingTop: '80px'
          }}
        >
        <Container>
          <Row className="align-items-center">
            <Col lg={6}></Col>
            <Col lg={6}>
              <div className="hero-content text-white">
                  <h1 className="display-4 fw-bold mb-4">
                    Business Operations, <span className="text-primary">Unified.</span>
                  </h1>
                  <p className="lead mb-3">
                    We empower our own brands with secure, integrated tools for client management, team access, and internal communications - all built with enterprise-grade reliability and compliance.
                  </p>
                  <p className="lead mb-4" style={{ fontStyle: 'italic' }}>
                    Kross Global is used internally across our network and is available by invitation only to ensure controlled, verified use.
                  </p>
                  <div className="d-flex gap-3 mb-4">
                    <Button 
                      size="lg" 
                      variant="primary"
                      onClick={scrollToInvite}
                      className="px-4"
                    >
                      Guest Invite Only
                      <ArrowRight className="ms-2" />
                    </Button>
                    {/* Watch Demo button - commented out per request */}
                    {/* <Button 
                      size="lg" 
                      variant="outline-light"
                      className="px-4"
                    >
                      <Play className="me-2" />
                      Watch Demo
                    </Button> */}
                  </div>
                  {/* Credit card and free trial text - commented out per request */}
                  {/* <div className="d-flex align-items-center text-white">
                    <CheckCircle className="text-success me-2" />
                    <span>No credit card required</span>
                    <CheckCircle className="text-success ms-4 me-2" />
                    <span>14-day free trial</span>
                  </div> */}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Powerful Features</h2>
              <p className="lead text-muted">
                Everything our brands need to operate securely and coordinate work in one controlled platform.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="feature-card text-center p-4">
                <div className="feature-icon bg-danger text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <People size={24} />
                </div>
                <h4 className="mb-3">Client Relationship Management (CRM)</h4>
                <p className="text-muted">
                  Track clients, partners, and accounts with centralized contacts, history, and relationship context for compliant engagement.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card text-center p-4">
                <div className="feature-icon bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <Globe2 size={24} />
                </div>
                <h4 className="mb-3">
                  Employee&nbsp;Relationship&nbsp;Management
                  <br />
                  (ERM)
                </h4>
                <p className="text-muted">
                  Coordinate teams, tasks, ownership, and roadmaps with clear queues, priorities, and progress visibility.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card text-center p-4">
                <div className="feature-icon bg-info text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <Inbox size={24} />
                </div>
                <h4 className="mb-3">
                  Company&nbsp;Internal&nbsp;Communications
                  <br />
                  (CIC)
                </h4>
                <p className="text-muted">
                  Run verified day-to-day communications with secure internal messaging and reliable company email.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card text-center p-4">
                <div className="feature-icon bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <Shield size={24} />
                </div>
                <h4 className="mb-3">Enterprise Security & Compliance</h4>
                <p className="text-muted">
                  Protect operations with role-based access, temporary credentials, audit logging, and environment isolation.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card text-center p-4">
                <div className="feature-icon bg-info text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <Envelope size={24} />
                </div>
                <h4 className="mb-3">Brand & External Communications</h4>
                <p className="text-muted">
                  Organize and manage approved brand-related communications and materials in a centralized, internal system.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card text-center p-4">
                <div className="feature-icon bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <Lightning size={24} />
                </div>
                <h4 className="mb-3">Workflow & Process Automation</h4>
                <p className="text-muted">
                  Automate workflows, approvals, notifications, and scheduling to reduce manual overhead as operations scale.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 
        ============================================
        PRICING SECTION - HIDDEN (INVITE-ONLY MODE)
        ============================================
        To restore pricing section:
        1. Uncomment this entire section below
        2. Uncomment the "Pricing" nav link in the navigation bar above
        3. Remove this comment block
        ============================================
      */}
      {/* Pricing Section - COMMENTED OUT */}
      {/* <section id="pricing" className="py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Simple Pricing</h2>
              <p className="lead text-muted">
                Choose the plan that fits your business needs
              </p>
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            <Col md={4}>
              <div className="pricing-card border rounded p-4 h-100">
                <div className="text-center mb-4">
                  <h4 className="fw-bold">Starter</h4>
                  <div className="display-4 fw-bold text-primary">$29</div>
                  <small className="text-muted">per month</small>
                </div>
                <ul className="list-unstyled">
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Up to 10,000 digital interactions/month</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Basic analytics</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Campaign templates</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Domain management (1 domain)</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Email support</li>
                </ul>
                <Button variant="outline-primary" className="w-100" onClick={handleSignIn}>
                  Guest Invite Only
                </Button>
              </div>
            </Col>
            <Col md={4}>
              <div className="pricing-card border border-primary rounded p-4 h-100 position-relative">
                <div className="position-absolute top-0 start-50 translate-middle">
                  <span className="badge bg-primary">Most Popular</span>
                </div>
                <div className="text-center mb-4">
                  <h4 className="fw-bold">Professional</h4>
                  <div className="display-4 fw-bold text-primary">$99</div>
                  <small className="text-muted">per month</small>
                </div>
                <ul className="list-unstyled">
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Up to 100,000 digital interactions/month</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Advanced analytics</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Custom templates</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Domain management (5 domains)</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Company email hosting (10 inboxes)</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Priority support</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />API access</li>
                </ul>
                <Button variant="primary" className="w-100" onClick={handleSignIn}>
                  Guest Invite Only
                </Button>
              </div>
            </Col>
            <Col md={4}>
              <div className="pricing-card border rounded p-4 h-100">
                <div className="text-center mb-4">
                  <h4 className="fw-bold">Enterprise</h4>
                  <div className="display-4 fw-bold text-primary">Custom</div>
                  <small className="text-muted">contact us</small>
                </div>
                <ul className="list-unstyled">
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Unlimited digital interactions</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Custom analytics</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Unlimited domain management</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Unlimited company email hosting</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />White-label solution</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Dedicated support</li>
                  <li className="mb-2"><CheckCircle className="text-success me-2" />Custom integrations</li>
                </ul>
                <Button variant="outline-primary" className="w-100" onClick={handleSignIn}>
                  Contact Sales
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section> */}
      {/* End of commented pricing section - To restore, uncomment above and remove this comment */}

      {/* About Section */}
      <section id="about" className="py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2 className="display-5 fw-bold mb-4">Built for Modern Business</h2>
              <p className="lead mb-4">
                Kross Global is an internal-use platform designed to help organizations operate securely, consistently, and efficiently across teams and brands.
              </p>
            </Col>
            <Col lg={6}>
              <div className="text-center">
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="display-4 fw-bold text-primary">99.9%</div>
                    <div className="text-muted">Uptime</div>
                  </div>
                  <div className="stat-item">
                    <div className="display-4 fw-bold text-success">24/7</div>
                    <div className="text-muted">Support</div>
                  </div>
                  <div className="stat-item">
                    <div className="display-4 fw-bold text-info">100%</div>
                    <div className="text-muted">Compliance</div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section id="invite-access" className="py-5 bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Invite‑Only Access</h2>
              <p className="lead mb-4">
                Our system is currently private, supporting our own brands and select partners through verified, invite-only access.
              </p>
              <Button
                size="lg"
                variant="light"
                className="px-5"
                onClick={openContact}
              >
                Request Access
                <ArrowRight className="ms-2" />
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 bg-dark text-white">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <Logo size="small" className="text-white" />
            </Col>
            <Col md={6} className="text-md-end">
              <div>
                <a href="#contact" onClick={(e) => { e.preventDefault(); setShowContactModal(true); }} className="text-white me-3">Contact Us</a>
                <small className="text-muted">
                  © 2024 KrossGlobal Marketing. All rights reserved.
                </small>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* Contact Modal */}
      <Modal show={showContact} onHide={closeContact} centered>
        <Modal.Header closeButton>
          <Modal.Title>Request Access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitted ? (
            <Alert variant="success" className="mb-0">
              Thanks for your request. We received your details and will respond with next steps.
            </Alert>
          ) : (
            <Form onSubmit={submitContact}>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      value={contact.firstName}
                      onChange={(e) => setContact({ ...contact, firstName: e.target.value })}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      value={contact.lastName}
                      onChange={(e) => setContact({ ...contact, lastName: e.target.value })}
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Control
                  value={contact.company}
                  onChange={(e) => setContact({ ...contact, company: e.target.value })}
                  required
                />
              </Form.Group>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Work Number</Form.Label>
                    <Form.Control
                      value={contact.workPhone}
                      onChange={(e) => setContact({ ...contact, workPhone: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      value={contact.mobilePhone}
                      onChange={(e) => setContact({ ...contact, mobilePhone: e.target.value })}
                    />
                  </Form.Group>
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  value={contact.address}
                  onChange={(e) => setContact({ ...contact, address: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Reason for Request</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={contact.reason}
                  onChange={(e) => setContact({ ...contact, reason: e.target.value })}
                  required
                />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={closeContact} disabled={submitting}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send Message'}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* Contact Us Modal (basic) */}
      <Modal show={showContactModal} onHide={() => { setShowContactModal(false); setSentContact(false); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Contact Us</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sentContact ? (
            <Alert variant="success" className="mb-0">Thanks for reaching out. We’ll get back to you shortly.</Alert>
          ) : (
            <Form onSubmit={async (e) => {
              e.preventDefault();
              setSendingContact(true);
              try {
                const apiUrl = process.env.REACT_APP_API_URL;
                const payload = { ...contactBasic, submittedAt: new Date().toISOString() };
                if (apiUrl) {
                  await fetch(`${apiUrl}/contact-requests`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                  }).catch(() => {});
                }
                setSentContact(true);
                setContactBasic({ name: '', email: '', message: '' });
              } finally {
                setSendingContact(false);
              }
            }}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control value={contactBasic.name} onChange={(e) => setContactBasic({ ...contactBasic, name: e.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={contactBasic.email} onChange={(e) => setContactBasic({ ...contactBasic, email: e.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Message</Form.Label>
                <Form.Control as="textarea" rows={4} value={contactBasic.message} onChange={(e) => setContactBasic({ ...contactBasic, message: e.target.value })} required />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => setShowContactModal(false)} disabled={sendingContact}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={sendingContact}>{sendingContact ? 'Sending…' : 'Send'}</Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* Access Code Signup Modal */}
      <Modal show={showCodeModal} onHide={() => setShowCodeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Your Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Enter your access code, email, and a password to create your account. After your
            first sign-in you won't need the access code again. You can request an
            access code <a href="#invite-access" onClick={(e) => { e.preventDefault(); setShowCodeModal(false); openContact(); }}>here</a>.
          </p>
          <Form onSubmit={handleInviteSignup}>
            <Form.Group className="mb-3">
              <Form.Label>Access Code</Form.Label>
              <Form.Control
                value={inviteCode}
                onChange={(e) => { setInviteCode(e.target.value); setInviteError(''); }}
                placeholder="e.g. CODE123"
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                value={inviteEmail}
                onChange={(e) => { setInviteEmail(e.target.value); setInviteError(''); }}
                placeholder="you@company.com"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={invitePassword}
                  onChange={(e) => { setInvitePassword(e.target.value); setInviteError(''); }}
                  placeholder="Create a strong password"
                  minLength={8}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ borderColor: '#ced4da' }}
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </Button>
              </InputGroup>
              {invitePassword.length > 0 && <PasswordChecklist password={invitePassword} />}
            </Form.Group>
            {inviteError && (
              <Alert variant="danger" className="py-2">{inviteError}</Alert>
            )}
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowCodeModal(false)} disabled={inviteSubmitting}>Cancel</Button>
              <Button type="submit" variant="primary" disabled={inviteSubmitting}>
                {inviteSubmitting ? 'Creating account…' : 'Create Account'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default LandingPage;
